import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import ChainedBackend from 'i18next-chained-backend'
import LocalStorageBackend from 'i18next-localstorage-backend'
import HttpBackend from 'i18next-http-backend'

const SUPPORTED_LANGS = ['en', 'es', 'pt', 'zh', 'hi', 'tl', 'fr'] as const
const NAMESPACES = ['common', 'marketing', 'app', 'tools', 'openclaw'] as const
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? (import.meta.env.DEV ? `${Date.now()}` : 'dev')

const commonConfig = {
  fallbackLng: 'en',
  defaultNS: 'common',
  fallbackNS: 'common',
  ns: NAMESPACES,
  interpolation: { escapeValue: false },
} as const

// SSR: load all translations upfront so the server can render any language.
// Vite replaces import.meta.env.SSR with `false` in the client build, so this
// entire block is dead-code-eliminated and never included in the browser bundle.
if (import.meta.env.SSR) {
  // import.meta.glob with eager:true is statically analyzed by Vite — all matched
  // files are bundled and available synchronously, no dynamic import issues.
  const modules = import.meta.glob('../locales/**/*.json', { eager: true }) as Record<string, { default: Record<string, unknown> }>

  const resources: Record<string, Record<string, unknown>> = {}
  for (const [path, mod] of Object.entries(modules)) {
    const match = path.match(/\/locales\/([^/]+)\/([^/]+)\.json$/)
    if (!match) continue
    const [, lang, ns] = match
    resources[lang] ??= {}
    resources[lang][ns] = mod.default
  }

  await i18n.use(initReactI18next).init({
    ...commonConfig,
    lng: 'en',
    resources,
  })
}

// Client: read the language from the URL path (always /{lang}/... after the root
// redirect) and lazy-load only that language via a localStorage → HTTP cache chain.
// Top-level await means i18n is fully initialised before any importing module runs,
// so components always have translations available on first render.
if (!import.meta.env.SSR) {
  // In dev, skip localStorage and use a startup-time cache buster so locale
  // edits are fetched immediately after a refresh.
  const backends = import.meta.env.DEV
    ? [HttpBackend]
    : [LocalStorageBackend, HttpBackend]
  const backendOptions = import.meta.env.DEV
    ? [{ loadPath: `/locales/{{lng}}/{{ns}}.json?v=${APP_VERSION}` }]
    : [
        {
          // localStorage cache invalidated on each deploy via version key.
          prefix: 'i18n_',
          expirationTime: 7 * 24 * 60 * 60 * 1000, // 1 week
          versions: Object.fromEntries(SUPPORTED_LANGS.map(l => [l, APP_VERSION])),
        },
        {
          loadPath: `/locales/{{lng}}/{{ns}}.json?v=${APP_VERSION}`,
        },
      ]

  await i18n
    .use(ChainedBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      ...commonConfig,
      debug: import.meta.env.DEV,
      backend: {
        backends,
        backendOptions,
      },
      // URL path beats stored preference beats browser language.
      // caches: [] — only LanguageSwitcher writes to localStorage explicitly.
      detection: {
        order: ['path', 'localStorage', 'navigator'],
        lookupFromPathIndex: 0,
        lookupLocalStorage: 'phonesalad-language',
        caches: [],
      },
    })
}

export default i18n
